import React, { useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, Code, Eye, Edit } from 'lucide-react';

type Language = 'typescript' | 'python' | 'curl' | 'javascript' | 'java' | 'csharp' | 'go' | 'php';

interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language: Language;
  onLanguageChange?: (language: Language) => void;
  placeholder?: string;
  minHeight?: number;
  readOnly?: boolean;
  showLanguageSelector?: boolean;
  label?: string;
}

const LANGUAGES: { value: Language; label: string }[] = [
  { value: 'typescript', label: 'TypeScript' },
  { value: 'javascript', label: 'JavaScript' },
  { value: 'python', label: 'Python' },
  { value: 'curl', label: 'cURL' },
  { value: 'java', label: 'Java' },
  { value: 'csharp', label: 'C#' },
  { value: 'go', label: 'Go' },
  { value: 'php', label: 'PHP' },
];

export function CodeEditor({
  value,
  onChange,
  language,
  onLanguageChange,
  placeholder = '// Digite seu código aqui...',
  minHeight = 200,
  readOnly = false,
  showLanguageSelector = true,
  label,
}: CodeEditorProps) {
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>(readOnly ? 'preview' : 'edit');
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-3">
          {label && (
            <span className="text-sm text-gray-300">{label}</span>
          )}
          
          {showLanguageSelector && onLanguageChange && (
            <select
              value={language}
              onChange={(e) => onLanguageChange(e.target.value as Language)}
              className="bg-gray-700 text-gray-200 text-sm rounded px-2 py-1 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={readOnly}
            >
              {LANGUAGES.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.label}
                </option>
              ))}
            </select>
          )}
          
          {!showLanguageSelector && (
            <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
              {LANGUAGES.find(l => l.value === language)?.label || language}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Copy Button */}
          <button
            type="button"
            onClick={copyToClipboard}
            className="p-1.5 text-gray-400 hover:text-white transition-colors"
            title="Copiar código"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-400" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </button>

          {/* View Mode Toggle */}
          {!readOnly && (
            <div className="flex items-center bg-gray-700 rounded p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('edit')}
                className={`p-1 rounded transition-colors ${
                  viewMode === 'edit' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                title="Modo Edição"
              >
                <Edit className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('preview')}
                className={`p-1 rounded transition-colors ${
                  viewMode === 'preview' ? 'bg-gray-600 text-white' : 'text-gray-400 hover:text-white'
                }`}
                title="Modo Preview"
              >
                <Eye className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Editor/Preview Area */}
      <div style={{ minHeight }}>
        {viewMode === 'edit' && !readOnly ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full h-full p-4 font-mono text-sm bg-gray-900 text-gray-100 resize-none focus:outline-none"
            style={{ minHeight }}
            spellCheck={false}
          />
        ) : (
          <div className="relative">
            {value ? (
              <SyntaxHighlighter
                language={language === 'curl' ? 'bash' : language}
                style={oneDark}
                customStyle={{
                  margin: 0,
                  borderRadius: 0,
                  minHeight,
                  fontSize: '0.875rem',
                }}
                showLineNumbers
              >
                {value}
              </SyntaxHighlighter>
            ) : (
              <div 
                className="flex items-center justify-center text-gray-500 bg-gray-900"
                style={{ minHeight }}
              >
                <Code className="w-8 h-8 mr-2 opacity-50" />
                <span>Nenhum código</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CodeEditor;
